import React from 'react';
import {
    Box, Typography, Collapse, ButtonBase, IconButton, TextField, Chip, ToggleButton,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import {
    WEEKDAY_SHORT, WEEKDAY_LONG, maskToDays, daysToMask, formatSchedule,
} from '../utils/schedule';

/**
 * "How often?" picker for a delivery plan.
 *
 * value:    { f_schedule_type, f_schedule_value, f_anchor_date }
 * onChange: called with the same shape whenever anything changes
 *
 * Plain words only — vendors read this, not developers. "How often?", not "Frequency".
 * "Every 2 days", not "Bi-daily". "Starting from", not "Anchor date".
 */

// Kept short enough to sit in a half-width button at 390px without truncating.
const CHOICES = [
    { type: 'daily', label: 'Every day' },
    { type: 'interval', label: 'Every few days' },
    { type: 'weekly', label: 'Days of the week' },
    { type: 'monthly', label: 'Once a month' },
];

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Defaults chosen so switching type always lands on something valid and sensible.
const DEFAULTS = {
    daily: { f_schedule_value: 1, f_anchor_date: null },
    interval: { f_schedule_value: 2, f_anchor_date: todayISO() },
    weekly: { f_schedule_value: 0, f_anchor_date: null },    // nothing ticked — the vendor picks
    monthly: { f_schedule_value: 1, f_anchor_date: null },
};

function Stepper({ value, min, max, onChange, suffix }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <IconButton
                size="small"
                onClick={() => onChange(Math.max(min, value - 1))}
                disabled={value <= min}
                sx={{ border: 1, borderColor: 'divider', width: 44, height: 44 }}
            >
                <Remove fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 40, textAlign: 'center' }}>
                {value}
            </Typography>
            <IconButton
                size="small"
                onClick={() => onChange(Math.min(max, value + 1))}
                disabled={value >= max}
                sx={{ border: 1, borderColor: 'divider', width: 44, height: 44 }}
            >
                <Add fontSize="small" />
            </IconButton>
            {suffix && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>{suffix}</Typography>
            )}
        </Box>
    );
}

export default function SchedulePicker({ value, onChange, disabled = false }) {
    const type = value?.f_schedule_type || 'daily';
    const val = Number(value?.f_schedule_value ?? 1);
    const anchor = value?.f_anchor_date || null;

    const set = (patch) => onChange({
        f_schedule_type: type, f_schedule_value: val, f_anchor_date: anchor, ...patch,
    });

    const pickType = (nextType) => {
        if (nextType === type) return;
        onChange({ f_schedule_type: nextType, ...DEFAULTS[nextType] });
    };

    const selectedDays = maskToDays(val);
    const toggleDay = (dayIdx) => {
        const next = selectedDays.includes(dayIdx)
            ? selectedDays.filter((d) => d !== dayIdx)
            : [...selectedDays, dayIdx].sort();
        set({ f_schedule_value: daysToMask(next) });
    };

    const anchorLabel = anchor
        ? new Date(`${anchor}T00:00:00`).toLocaleDateString(undefined,
            { weekday: 'short', day: 'numeric', month: 'short' })
        : '';

    const previewLabel = formatSchedule({ f_schedule_type: type, f_schedule_value: val });
    const nothingPicked = type === 'weekly' && !val;

    return (
        <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            {/* The answer rides along with the question, so the vendor always sees what
                they have chosen without a separate summary box below the fold. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary">How often?</Typography>
                <Chip
                    size="small"
                    label={nothingPicked ? 'Pick at least one day' : previewLabel}
                    sx={{
                        height: 22, fontSize: '0.7rem', fontWeight: 700,
                        bgcolor: nothingPicked ? '#ffebee' : '#f3e5f5',
                        color: nothingPicked ? '#c62828' : '#6a1b9a',
                    }}
                />
            </Box>

            {/* 2x2 so four options fit a 390px screen without truncating any label */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {CHOICES.map((c) => (
                    <ToggleButton
                        key={c.type}
                        value={c.type}
                        selected={type === c.type}
                        onChange={() => pickType(c.type)}
                        sx={{
                            minHeight: 46, px: 1, textTransform: 'none', fontSize: '0.78rem',
                            fontWeight: type === c.type ? 700 : 500, lineHeight: 1.2,
                            borderColor: type === c.type ? '#7b1fa2' : 'grey.300',
                            '&.Mui-selected': {
                                bgcolor: '#7b1fa2', color: '#fff',
                                '&:hover': { bgcolor: '#6a1b9a' },
                            },
                        }}
                    >
                        {c.label}
                    </ToggleButton>
                ))}
            </Box>

            {/* Every few days */}
            <Collapse in={type === 'interval'}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">Deliver every</Typography>
                    <Stepper value={val} min={2} max={60} suffix="days"
                             onChange={(v) => set({ f_schedule_value: v })} />
                    <TextField
                        fullWidth type="date" label="Starting from" sx={{ mt: 2 }}
                        value={anchor || todayISO()}
                        onChange={(e) => set({ f_anchor_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                    {anchor && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            First delivery on {anchorLabel}, then every {val} days.
                        </Typography>
                    )}
                </Box>
            </Collapse>

            {/* Days of the week */}
            <Collapse in={type === 'weekly'}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">Which days?</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, justifyContent: 'space-between' }}>
                        {WEEKDAY_SHORT.map((short, idx) => {
                            const on = selectedDays.includes(idx);
                            return (
                                <ButtonBase
                                    key={idx}
                                    onClick={() => toggleDay(idx)}
                                    aria-label={WEEKDAY_LONG[idx]}
                                    sx={{
                                        width: 42, height: 42, borderRadius: '50%',
                                        border: 1, borderColor: on ? '#7b1fa2' : 'grey.300',
                                        bgcolor: on ? '#7b1fa2' : 'transparent',
                                        color: on ? '#fff' : 'text.primary',
                                        fontWeight: on ? 'bold' : 'normal', fontSize: 12,
                                        transition: 'background-color 0.15s, border-color 0.15s',
                                    }}
                                >
                                    {short}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>
            </Collapse>

            {/* Once a month */}
            <Collapse in={type === 'monthly'}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">On which date?</Typography>
                    <Stepper value={val} min={1} max={31}
                             onChange={(v) => set({ f_schedule_value: v })} />
                    {val > 28 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            In February this will be the last day of the month.
                        </Typography>
                    )}
                </Box>
            </Collapse>

        </Box>
    );
}
